/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { recordProperties } from 'containers/Base/styles';
import type { HTMLAttributes } from 'react';
import type { TSchema } from 'types/items';
import type { WorkingState } from '../../Data/data.types';

export const decimalToTimeString = (decimal: number): string => {
  if (decimal === null || decimal === undefined || isNaN(decimal)) return '';
  const hours = Math.floor(decimal);
  const minutes = Math.round((decimal - hours) * 60);
  return `${hours.toString().padStart(2, '0')}:${minutes
    .toString()
    .padStart(2, '0')}`;
};

const timeStringToDecimal = (timeString: string): number => {
  if (!timeString || !timeString.includes(':')) return 0;
  const [hoursStr, minutesStr] = timeString.split(':');
  const hours = parseInt(hoursStr, 10) || 0;
  const minutes = parseInt(minutesStr, 10) || 0;
  return hours + minutes / 60;
};

interface Props extends HTMLAttributes<HTMLDivElement> {
  fields: any[];
  mode: 'add' | 'edit';
  record: any;
  setRecord: (updatedRecord: any) => void;
  schema: TSchema<any>;
  workingState: WorkingState;
}

export default function ShiftProperties({
  fields,
  mode,
  record,
  schema,
  setRecord,
  workingState,
}: Props) {
  const { classes } = recordProperties();
  const [localTimeValues, setLocalTimeValues] = useState<{
    [key: string]: string;
  }>({});
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  useEffect(() => {
    const timeFields = ['startTime', 'endTime'];
    const newLocalValues: { [key: string]: string } = {};

    timeFields.forEach((fieldName) => {
      if (record[fieldName] !== undefined && record[fieldName] !== null) {
        const timeString = decimalToTimeString(record[fieldName]);
        if (
          !localTimeValues[fieldName] ||
          Math.abs(
            timeStringToDecimal(localTimeValues[fieldName]) - record[fieldName]
          ) > 0.01
        ) {
          newLocalValues[fieldName] = timeString;
        } else {
          newLocalValues[fieldName] = localTimeValues[fieldName];
        }
      }
    });

    setLocalTimeValues((prev) => ({ ...prev, ...newLocalValues }));
  }, [record.startTime, record.endTime]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'startTime' || name === 'endTime') {
      const cursorPosition = e.target.selectionStart;

      setLocalTimeValues((prev) => ({ ...prev, [name]: value }));

      if (value.includes(':') && value.length >= 4) {
        const decimalValue = timeStringToDecimal(value);
        setRecord({ ...record, [name]: decimalValue });
      }

      setTimeout(() => {
        const input = inputRefs.current[name];
        if (input && cursorPosition !== null) {
          input.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
    } else {
      setRecord({ ...record, [name]: value });
    }
  };

  const handleTimeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'startTime' || name === 'endTime') {
      const decimalValue = timeStringToDecimal(value);
      setRecord({ ...record, [name]: decimalValue });
      const formattedTime = decimalToTimeString(decimalValue);
      setLocalTimeValues((prev) => ({
        ...prev,
        [name]: formattedTime,
      }));
    }
  };

  return (
    <div>
      {fields
        .filter((f) => f.required !== 'A' && f.uiType !== 'none')
        .map((field, idx) => (
          <div key={field.fieldName}>
            <Tooltip
              title={field.description}
              disableHoverListener
              placement="top-end"
            >
              <div>
                <TextField
                  inputRef={(ref) => {
                    if (
                      field.fieldName === 'startTime' ||
                      field.fieldName === 'endTime'
                    ) {
                      inputRefs.current[field.fieldName] = ref;
                    }
                  }}
                  autoFocus={idx === 0}
                  disabled={
                    workingState?.adding ||
                    workingState?.updating ||
                    (field.fieldName === schema?.hierarchy?.primaryKey &&
                      mode === 'edit') ||
                    field.editable !== 'true'
                  }
                  required={field.required === 'Y'}
                  name={field.fieldName}
                  label={
                    field.fieldName === 'shiftNum'
                      ? 'Shift ID'
                      : field.fieldName === 'startTime'
                      ? 'Start Time (HH:MM)'
                      : field.fieldName === 'endTime'
                      ? 'End Time (HH:MM)'
                      : field.label
                  }
                  value={
                    field.fieldName === 'startTime' ||
                    field.fieldName === 'endTime'
                      ? localTimeValues[field.fieldName] ?? ''
                      : record[field.fieldName] ?? ''
                  }
                  onChange={handleInputChange}
                  onBlur={
                    field.fieldName === 'startTime' ||
                    field.fieldName === 'endTime'
                      ? handleTimeBlur
                      : undefined
                  }
                  fullWidth
                  className={classes.textField}
                  placeholder={
                    field.fieldName === 'startTime' ||
                    field.fieldName === 'endTime'
                      ? 'e.g., 08:30 or 14:30'
                      : undefined
                  }
                />
              </div>
            </Tooltip>
          </div>
        ))}
    </div>
  );
}
